import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { MailCheck, Search, RefreshCw, Send, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', slate: '#5C6B7A', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020' };
const STATUS = { sent: C.green, failed: C.red, skipped: C.amber };

export default function EmailLogPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(null);

  const resend = async (r) => {
    setBusy(r.log_id);
    try {
      const res = await api.post(`/platform/email-log/${r.log_id}/resend`);
      toast.success(res.data.message);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Resend failed'); }
    setBusy(null);
  };

  const [dismissing, setDismissing] = useState(null);
  const [reason, setReason] = useState('');
  const dismiss = async (r) => {
    setBusy(r.log_id);
    try {
      await api.post(`/platform/email-log/${r.log_id}/dismiss`, { reason });
      toast.success('Closed with the reason on file — kept in the log, never deleted');
      setDismissing(null); setReason('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Dismiss failed'); }
    setBusy(null);
  };

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/platform/email-log?q=${encodeURIComponent(q)}&status=${status}&limit=200`)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [api, q, status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="email-log-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: C.navy, fontFamily: 'Georgia, serif' }}>Email Delivery Log</h1>
          <p className="text-sm mt-1" style={{ color: C.slate }}>Every outbound email — who got what, and whether it went out.</p>
        </div>
        {data && (
          <div className="ml-auto flex gap-2" data-testid="email-log-week-stats">
            {['sent', 'failed', 'skipped'].map(s => (
              <span key={s} className="text-[11px] font-semibold px-2.5 py-1 rounded-sm"
                style={{ background: `${STATUS[s]}14`, color: STATUS[s] }}>
                {data.week[s]} {s} · 7d
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: C.slate }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search recipient or subject..."
            data-testid="email-log-search"
            className="w-full pl-8 pr-3 py-2 text-sm rounded-sm border focus:outline-none"
            style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} />
        </div>
        <select value={status} onChange={e => setStatus(e.target.value)} data-testid="email-log-status-filter"
          className="px-2.5 py-2 text-sm rounded-sm border focus:outline-none"
          style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }}>
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="skipped">Skipped</option>
        </select>
        <button onClick={load} data-testid="email-log-refresh"
          className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-semibold"
          style={{ color: C.slate, border: '1px solid rgba(136,150,168,0.3)' }}>
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      {loading ? <Skeleton className="h-64 w-full" /> : (
        <Card className="p-0 rounded-sm overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.14)' }}>
          <table className="w-full text-left">
            <thead><tr style={{ background: 'rgba(136,150,168,0.06)' }}>
              {['When', 'Recipient', 'Subject', 'Status', 'Detail', ''].map((h, hi) => (
                <th key={hi} className="py-2 px-3 text-[9px] uppercase tracking-wider font-bold" style={{ color: C.slate }}>{h}</th>))}
            </tr></thead>
            <tbody data-testid="email-log-table">
              {(data?.rows || []).map((r, i) => (
                <tr key={i} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.08)' }}>
                  <td className="py-2 px-3 text-[11px] whitespace-nowrap" style={{ color: C.slate }}>
                    {new Date(r.at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="py-2 px-3 text-xs font-medium" style={{ color: C.navy }}>{r.to}</td>
                  <td className="py-2 px-3 text-xs max-w-[380px] truncate" style={{ color: C.navy }} title={r.subject}>
                    {r.subject}{r.attachment && <span className="ml-1 text-[9px]" style={{ color: C.blue }}>📎</span>}
                  </td>
                  <td className="py-2 px-3">
                    <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm"
                      style={{ background: `${STATUS[r.status] || C.slate}14`, color: STATUS[r.status] || C.slate }}>{r.status}</span>
                  </td>
                  <td className="py-2 px-3 text-[10px] max-w-[220px] truncate" style={{ color: r.error ? C.red : C.slate }} title={r.error || r.email_id}>
                    {r.error || r.email_id || '—'}
                  </td>
                  <td className="py-2 px-3 text-right">
                    {(r.status === 'failed' || r.status === 'skipped') && !r.resolved && r.log_id && (
                      dismissing === r.log_id ? (
                        <span className="inline-flex items-center gap-1" data-testid={`email-dismiss-form-${r.log_id}`}>
                          <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Why it can never be resent (10+ chars)"
                            data-testid={`email-dismiss-reason-${r.log_id}`}
                            className="px-2 py-1 text-[10px] rounded-sm border w-56 focus:outline-none" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} />
                          <button onClick={() => dismiss(r)} disabled={busy === r.log_id || reason.trim().length < 10}
                            data-testid={`email-dismiss-confirm-${r.log_id}`}
                            className="px-2 py-1 rounded-sm text-[10px] font-semibold disabled:opacity-50" style={{ background: `${C.red}14`, color: C.red }}>Close it</button>
                          <button onClick={() => { setDismissing(null); setReason(''); }} data-testid={`email-dismiss-cancel-${r.log_id}`}
                            className="px-1.5 py-1 text-[10px]" style={{ color: C.slate }}>Cancel</button>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5">
                          <button onClick={() => resend(r)} disabled={busy === r.log_id} data-testid={`email-resend-${r.log_id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-semibold disabled:opacity-50"
                            style={{ background: 'rgba(26,76,124,0.08)', color: C.blue }}>
                            <Send className="w-3 h-3" /> Resend
                          </button>
                          <button onClick={() => { setDismissing(r.log_id); setReason(''); }} data-testid={`email-dismiss-${r.log_id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-semibold"
                            style={{ background: `${C.slate}14`, color: C.slate }}>
                            <XCircle className="w-3 h-3" /> Dismiss
                          </button>
                        </span>
                      )
                    )}
                    {r.resolved && r.dismissed && (
                      <span className="text-[9px] font-semibold" style={{ color: C.slate }} title={r.dismissed.reason}
                        data-testid={`email-dismissed-${r.log_id}`}>DISMISSED · {r.dismissed.reason}</span>
                    )}
                    {r.resolved && !r.dismissed && <span className="text-[9px] font-semibold" style={{ color: C.green }}>RESENT</span>}
                  </td>
                </tr>
              ))}
              {(data?.rows || []).length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-xs" style={{ color: C.slate }}>
                  <MailCheck className="w-6 h-6 mx-auto mb-2 opacity-40" />No emails match — new sends appear here automatically.
                </td></tr>
              )}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
